import { zodResolver } from "@hookform/resolvers/zod";
import { Fragment, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { UserController } from "services/user/UserController";
import {
  Button,
  Card,
  Checkbox,
  Input,
  Link,
  PrivacyContent,
  TermsContent,
} from "stories";
import { PasswordChecker } from "stories/components/passwordSlider";
import { useFetchState } from "utils/customHooks/useFetchState";
import { z } from "zod";
import classes from "./SignupCard.module.scss";

export function conainsUpperCase(str) {
  return /[A-Z]/.test(str);
}

export function containsLowerCase(str) {
  return /[a-z]/.test(str);
}

export function containsNumber(str) {
  return /[1-9]/.test(str);
}

export function containsSpecialCharacters(str) {
  return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(str); //eslint-disable-line
}

const schema = z
  .object({
    fullName: z.string().min(1, "This field is required"),
    email: z.string().email("Please enter a valid email"),
    password: z
      .string()
      .min(8, "Please enter at least 8 characters")
      .refine(
        (str) => conainsUpperCase(str),
        "This field should contain at least 1 uppercase letter"
      )
      .refine(
        (str) => containsLowerCase(str),
        "This field should contain at least 1 lower letter"
      )
      .refine(
        (str) => containsNumber(str),
        "This field should contain at least 1 number"
      )
      .refine(
        (str) => containsSpecialCharacters(str),
        "One special character (!@#$%...) should be used in this field."
      ),
    passwordConfirm: z.string().min(1, "This field is required"),
    terms: z.boolean().refine((v) => v, "This field is required"),
    privacy: z.boolean().refine((v) => v, "This field is required"),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: "Passwords don't match",
    path: ["passwordConfirm"],
  });

const getDefaultValues = () => ({
  fullName: "",
  email: "",
  password: "",
  passwordConfirm: "",
  terms: false,
  privacy: false,
});

const modalTypes = {
  terms: {
    title: "Terms of Service",
    component: <TermsContent />,
  },
  privacy: {
    title: "Privacy Policy",
    component: <PrivacyContent />,
  },
};

export const SignupCard = () => {
  const {
    formState: { errors },
    ...form
  } = useForm({
    defaultValues: getDefaultValues(),
    resolver: zodResolver(schema),
  });
  const [activeModalOptions, setActiveModalOptions] = useState({
    visibility: false,
    type: undefined,
  });
  const navigate = useNavigate();
  const user = UserController();
  const [fetchState, sendRequest] = useFetchState();
  const password = useWatch({
    control: form.control,
    name: "password",
  });

  const handleModalButtonClick = () => {
    setActiveModalOptions({ ...activeModalOptions, visibility: false });
  };
  const handleUserSignUp = (requestBody) => {
    sendRequest(user.createUser, requestBody);
  };

  useEffect(() => {
    if (!fetchState.isLoading && !fetchState.isError && fetchState.data) {
      localStorage.setItem("token", fetchState.data.token);
      navigate("/dashboard");
    }
  }, [fetchState, navigate]);

  return (
    <Fragment>
      <div className={classes["wrapper"]}>
        <Card>
          <div className={classes["content-wrapper"]}>
            <form onSubmit={form.handleSubmit(handleUserSignUp)}>
              <div className={classes["title"]}>Create a Broker Account</div>
              <Input
                label={"Full Name"}
                placeholder={"John Doe"}
                {...form.register("fullName")}
                error={errors?.fullName?.message}
              />
              <Input
                label={"Email Address"}
                placeholder={"name@mail.com"}
                {...form.register("email")}
                error={errors?.email?.message}
              />
              <Input
                type={"password"}
                label={"Password"}
                placeholder={"Input password"}
                {...form.register("password")}
                error={errors?.password?.message}
              />
              <PasswordChecker password={password} />
              <Input
                type={"password"}
                label={"Repeat Password"}
                placeholder={"Input password"}
                {...form.register("passwordConfirm")}
                error={errors?.passwordConfirm?.message}
              />
              <div className={classes["checkbox-wrapper"]}>
                <Controller
                  name="terms"
                  control={form.control}
                  render={({ field }) => {
                    return (
                      <Checkbox
                        label={
                          <div>
                            I agree with the
                            <Link
                              to={""}
                              onClick={() =>
                                setActiveModalOptions({
                                  visibility: true,
                                  type: "terms",
                                })
                              }
                            >
                              {" "}
                              Terms of Service
                            </Link>
                          </div>
                        }
                        {...field}
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                        error={errors?.terms?.message}
                      />
                    );
                  }}
                />
                <Controller
                  name="privacy"
                  control={form.control}
                  render={({ field }) => {
                    return (
                      <Checkbox
                        label={
                          <div>
                            I agree with the
                            <Link
                              to={""}
                              onClick={() =>
                                setActiveModalOptions({
                                  visibility: true,
                                  type: "privacy",
                                })
                              }
                            >
                              {" "}
                              Privacy Policy
                            </Link>
                          </div>
                        }
                        {...field}
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                        error={errors?.privacy?.message}
                      />
                    );
                  }}
                />
                {/*<Checkbox*/}
                {/*    label={*/}
                {/*        <div>I agree with the <Link to={'#'}>Pricing Agreement</Link></div>*/}
                {/*    }*/}
                {/*/>*/}
              </div>
              <Button
                label={"Create My Broker Account"}
                loading={fetchState.isLoading}
              />
            </form>
          </div>
        </Card>
      </div>
      <Modal
        show={activeModalOptions.visibility}
        onHide={() =>
          setActiveModalOptions({ ...activeModalOptions, visibility: false })
        }
        size={"lg"}
        centered
      >
        <Modal.Header closeButton>
          <div className={"modal-title"}>
            {modalTypes[activeModalOptions.type]?.title}
          </div>
        </Modal.Header>
        <Modal.Body>
          {modalTypes[activeModalOptions.type]?.component}
          <div className="d-flex justify-content-end p-4">
            <div className="w-25 me-2">
              <Button
                label={"Reject"}
                variant={"danger"}
                onClick={() => handleModalButtonClick(false)}
              />
            </div>
            <div className="w-25">
              <Button
                label={"Accept"}
                onClick={() => handleModalButtonClick(true)}
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};
